import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthClient, AuthState } from './auth-client.ts';
import { hasFeature, hasRole, type Principal } from './principal.ts';

export type AuthContextValue = AuthState & {
  user: Principal | null;
  roles: readonly string[];
  tenantId: string | null;
  features: readonly string[];
  hasRole: (role: string) => boolean;
  hasFeature: (feature: string) => boolean;
  login: AuthClient['login'];
  logout: AuthClient['logout'];
  getAccessToken: AuthClient['getAccessToken'];
  handleCallback: AuthClient['handleCallback'];
  switchTenant: (organization: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ client, children }: { client: AuthClient; children: ReactNode }) => {
  const [state, setState] = useState<AuthState>(client.getState());

  useEffect(() => client.subscribe(setState), [client]);

  const switchTenant = useCallback(
    (organization: string) => client.login({ organization, returnTo: window.location.pathname }),
    [client],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      user: state.principal,
      roles: state.principal ? [...state.principal.realmRoles, ...state.principal.clientRoles] : [],
      tenantId: state.principal?.tenantId ?? null,
      features: state.principal?.features ?? [],
      hasRole: (role) => hasRole(state.principal, role),
      hasFeature: (feature) => hasFeature(state.principal, feature),
      login: client.login,
      logout: client.logout,
      getAccessToken: client.getAccessToken,
      handleCallback: client.handleCallback,
      switchTenant,
    }),
    [state, client, switchTenant],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth requires AuthProvider');
  return context;
};
